import {
  controlPoints,
  type CyberScoreResult,
  type MissionPlan,
  type Provenance,
  type ScoreTrace,
} from '@cyberscore/contracts';
import { CONTROL_LIBRARY } from '@cyberscore/contracts/library';

/**
 * What the business owner's pages derive from a score: how far along the journey they are, the milestones
 * they've earned, and the insurer's minimum requirements. Everything comes from the score and its trace.
 */

/** Evidence confidence at which a score counts as verified (the share insurers ask for). */
export const VERIFIED_AT = 0.6;
/** Evidence confidence for the "Verified business" milestone. */
export const WELL_VERIFIED_AT = 0.8;

export interface Level {
  key: 'check' | 'verified' | 'protected' | 'insurable' | 'certified';
  name: string;
  /** What reaching it takes. */
  blurb: string;
}

export const LEVELS: readonly Level[] = [
  { key: 'check', name: 'Cyber Check', blurb: 'Every required question answered' },
  { key: 'verified', name: 'Verified', blurb: `${VERIFIED_AT * 100}% of your answers backed by evidence` },
  { key: 'protected', name: 'Protected', blurb: 'Every insurer minimum in place' },
  { key: 'insurable', name: 'Insurable', blurb: 'Submitted to your insurer' },
  { key: 'certified', name: 'Certified', blurb: 'Independent review' },
];

export interface Requirement {
  control: string;
  name: string;
  state: 'met' | 'failed' | 'open';
  /** Points once it is fully in place. */
  points: number;
  /** The question that answers it, to link to. */
  question: string | null;
}

/**
 * The insurer's minimum requirements: the kill-switch controls of the assessment. Met when fully in place;
 * failed when answered as missing (it caps the score); open otherwise (partial, not sure, or unanswered).
 */
export function requirements(r: CyberScoreResult, trace: ScoreTrace): Requirement[] {
  const failed = new Set(r.kill_switches.failed.map((k) => k.control));
  const worth = controlPoints(trace);
  const order = { failed: 0, open: 1, met: 2 };
  return trace.controls
    .filter((c) => c.kill_switch)
    .map((c): Requirement => ({
      control: c.control,
      name: c.name,
      state: c.status === 'in_place' ? 'met' : failed.has(c.control) ? 'failed' : 'open',
      points: worth[c.control] ?? 0,
      question: c.questions[0] ?? null,
    }))
    .sort((a, b) => order[a.state] - order[b.state] || (a.control < b.control ? -1 : 1));
}

/**
 * How far along the journey the business is: the index of the level it is working towards (5 once all are
 * reached). Levels are reached in order. Certification needs an independent review, which nothing records
 * yet, so it is never reached.
 */
export function levelOf(s: { provisional: boolean; confidence: number; requirements: Requirement[]; submitted: boolean }): number {
  const reached = [
    !s.provisional,
    s.confidence >= VERIFIED_AT,
    s.requirements.length > 0 && s.requirements.every((q) => q.state === 'met'),
    s.submitted,
    false,
  ];
  const i = reached.findIndex((x) => !x);
  return i === -1 ? reached.length : i;
} // prettier-ignore

/** The milestone a mission earns once nothing in it is left to do. */
const MISSION_MILESTONES: Record<string, { name: string; blurb: string }> = {
  accounts: { name: 'Identity Protected', blurb: 'A stolen password is not enough to get in' },
  email: { name: 'Email Protected', blurb: 'Fake invoices and spoofed email are stopped' },
  ransomware: { name: 'Ransomware Ready', blurb: 'Backups you can restore from, out of reach' },
  internet: { name: 'Attack Surface Clean', blurb: 'Nothing open or unpatched facing the internet' },
  devices: { name: 'Devices Secured', blurb: 'Every computer known and locked down' },
  data: { name: 'Data Protected', blurb: 'Customer and staff data handled as PDPA expects' },
  watch: { name: 'Eyes On', blurb: 'Someone watches for the signs of an attack' },
  ready: { name: 'Incident Ready', blurb: 'A plan for the first hour, and people who know it' },
  suppliers: { name: 'Supplier Assured', blurb: 'Suppliers’ access limited and reviewed' },
  governance: { name: 'Security Led', blurb: 'Someone in charge, with written rules' },
  factory: { name: 'Factory Secured', blurb: 'Production kept apart from the office network' },
  patients: { name: 'Care Protected', blurb: 'Patient records private, and care goes on' },
  checkout: { name: 'Checkout Secured', blurb: 'Card data and the tills that take it protected' },
  fleet: { name: 'Goods Moving', blurb: 'Scanners, trucks and partner links secured' },
  regulator: { name: 'Regulator Ready', blurb: 'What RMiT asks of financial services firms' },
  rmit: { name: 'RMiT in Full', blurb: 'What RMiT asks of a licensed institution' },
  csa: { name: 'Act Ready', blurb: 'What the Cyber Security Act asks of an NCII entity' },
  clients: { name: 'Client Trusted', blurb: 'Client files and client money protected' },
  plant: { name: 'Plant Secured', blurb: 'Control systems kept apart and run safely by hand' },
  supply: { name: 'Supply Assured', blurb: 'Stations, meters and consoles locked down' },
  network: { name: 'Network Guarded', blurb: 'Management, number moves and call records protected' },
  public: { name: 'Public Trusted', blurb: 'Official information and officers’ access protected' },
  campus: { name: 'Campus Safe', blurb: 'Student records and shared computers protected' },
};

export interface Milestone {
  key: string;
  name: string;
  blurb: string;
  earned: boolean;
}

/** Milestones: one per mission of the assessment, plus verification and insurance readiness. Earned first. */
export function milestones(plans: MissionPlan[], confidence: number, reqs: Requirement[]): Milestone[] {
  const list: Milestone[] = [
    ...plans.map((m) => ({ key: m.id, ...(MISSION_MILESTONES[m.id] ?? { name: m.name, blurb: m.blurb }), earned: m.next.steps.length === 0 })),
    { key: 'verified', name: 'Verified Business', blurb: `Over ${WELL_VERIFIED_AT * 100}% of your answers backed by evidence`, earned: confidence >= WELL_VERIFIED_AT },
    { key: 'insurance', name: 'Insurance Ready', blurb: 'Every insurer minimum in place', earned: reqs.length > 0 && reqs.every((q) => q.state === 'met') },
  ]; // prettier-ignore
  return list.sort((a, b) => Number(b.earned) - Number(a.earned));
}

/** How an answer is known, in the owner's words. */
export const PROVENANCE_LABEL: Record<Provenance, string> = {
  USER_DECLARED: 'Self-declared',
  ADMIN_ENTERED: 'Entered by your insurer',
  INFERRED: 'Worked out from other answers',
  AI_EXTRACTED: 'Read from a document',
  DOCUMENT_VERIFIED: 'Document checked',
  SECURITY_SCAN: 'Security scan',
  EXTERNAL_PROVIDER: 'External source',
  API_DISCOVERED: 'Connected system',
};

/** The plain-language question the library asks for a control: what the control means for a business. */
export const controlQuestion = (id: string) => CONTROL_LIBRARY[id]?.question ?? null;

/** NIST CSF, CIS and ISO 27001 references of a control, for people who want the mapping. */
export function frameworks(id: string): string[] {
  const c = CONTROL_LIBRARY[id];
  if (!c) return [];
  const f = c.frameworks;
  return [
    `NIST CSF ${f.nist_csf}`,
    ...(f.cis ? [`CIS ${f.cis}`] : []),
    ...(f.iso_27001 ? [`ISO 27001 ${f.iso_27001}`] : []),
    ...(c.kill_switch ? ['Kill switch'] : []),
  ];
}
